import React, { useState, useRef, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { UploadSimple, FilePdf, FileCsv, Target, Sparkle, Trash, ArrowClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

/**
 * Sales Tools — Competitive Pricing Analyzer
 *
 * Upload a prospect's existing-provider transaction file (CSV/XLSX/PDF) OR
 * a competitor's price list (PDF/CSV) and the system extracts, matches, and
 * computes projected savings against getFuel's own rack prices.
 *
 * Two modes:
 *   transactions — has volumes, produces projected $ savings + annualised
 *   pricelist    — no volumes, produces $/L deltas per site + per city
 *
 * The backend does heavy lifting (extraction via Claude for PDFs, tiered
 * matching station→city→province, aggregation). Frontend just renders the
 * JSON result and lets the user export to PDF/CSV for pitch decks.
 */
export default function SalesTools() {
  const [mode, setMode] = useState("transactions");
  const [network, setNetwork] = useState("");     // "" (any) | "esso" | "pipeline"
  const [asOfDate, setAsOfDate] = useState("");   // YYYY-MM-DD — overrides per-row date
  const [customerLabel, setCustomerLabel] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [filename, setFilename] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const fileRef = useRef(null);

  // Helper: axios responseType:"blob" turns error payloads into blobs too,
  // so a 400/500 error body comes back unreadable unless we decode it.
  const readBlobError = async (e) => {
    try {
      const blob = e.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try { return JSON.parse(text).detail || text; } catch { return text; }
      }
    } catch { /* fall through */ }
    return e.response?.data?.detail || e.message || "Request failed";
  };

  const loadAnalyses = useCallback(async () => {
    try {
      const { data } = await api.get("/prospect-analyzer/analyses");
      setAnalyses(data.analyses || []);
    } catch (e) { /* silently ignore — the page still works without the list */ }
  }, []);

  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".csv") && !lower.endsWith(".xlsx") && !lower.endsWith(".xls") && !lower.endsWith(".pdf")) {
      toast.error("Please upload a CSV, XLSX, or PDF file");
      return;
    }
    setAnalyzing(true);
    setResult(null);
    setFilename(file.name);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    if (network) fd.append("network", network);
    if (asOfDate) fd.append("as_of_date", asOfDate);
    if (customerLabel) fd.append("customer_label", customerLabel);
    try {
      const { data } = await api.post("/prospect-analyzer/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      setResult(data);
      loadAnalyses();
      const n = data.summary?.row_count || data.detail?.length || data.by_station?.length || 0;
      if (n > 0) toast.success(`Analysed ${n} rows from ${file.name}`);
      else toast.warning("No rows could be extracted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const downloadReport = async (fmt) => {
    if (!result) return;
    try {
      const r = await api.post(`/prospect-analyzer/report/${fmt}`, {
        result, customer_label: customerLabel || null,
      }, { responseType: "blob", timeout: 60_000 });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const name = m ? m[1] : `savings-report.${fmt}`;
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.style.display = "none";
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(await readBlobError(e));
    }
  };

  const loadAnalysis = async (id) => {
    try {
      const { data } = await api.get(`/prospect-analyzer/analyses/${id}`);
      setResult(data);
      setFilename(data.filename);
      setCustomerLabel(data.customer_label || "");
      setMode(data.mode);
      setNetwork(data.network || "");
      setAsOfDate(data.as_of_date || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load analysis");
    }
  };

  const deleteAnalysis = async (id) => {
    if (!window.confirm("Delete this analysis? It's auto-deleted after 24h anyway.")) return;
    try {
      await api.delete(`/prospect-analyzer/analyses/${id}`);
      if (result?.analysis_id === id) setResult(null);
      loadAnalyses();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDeleteAnalyses = async () => {
    if (analyses.length === 0) return;
    if (!window.confirm(`Delete all ${analyses.length} saved analyses? This clears the recent-uploads list so you can focus on the next prospect.`)) return;
    try {
      const { data } = await api.post("/prospect-analyzer/analyses/bulk-delete", { confirm: "DELETE" });
      setResult(null);
      loadAnalyses();
      toast.success(`Deleted ${data.deleted} analyses`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  const s = result?.summary || {};
  const isPriceList = mode === "pricelist";

  return (
    <SectionPad>
      <PageHeader
        title="Sales Tools"
        subtitle="COMPETITIVE PRICING ANALYZER · upload a prospect's fuel bill, see what they'd save with getFuel"
      />

      {/* ---- Mode + upload ---- */}
      <div className="gf-card gf-card-body mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="overline mb-1">ANALYSIS MODE</div>
            <div className="flex gap-2">
              <button
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${mode === "transactions" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}
                onClick={() => { setMode("transactions"); setResult(null); }}
                data-testid="mode-transactions"
              >
                Transaction analysis
              </button>
              <button
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${mode === "pricelist" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}
                onClick={() => { setMode("pricelist"); setResult(null); }}
                data-testid="mode-pricelist"
              >
                Price list comparison
              </button>
            </div>
            <div className="text-xs text-zinc-500 mt-1 max-w-md">
              {mode === "transactions"
                ? "Actual fuelling history (CSV/XLSX/PDF). Has volumes — produces $ savings."
                : "Competitor's rack price list (PDF/XLSX). No volumes — produces $/L deltas per station."}
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="overline mb-1">PROSPECT / CUSTOMER LABEL (OPTIONAL)</div>
            <input
              className="gf-input"
              value={customerLabel}
              onChange={(e) => setCustomerLabel(e.target.value)}
              placeholder="e.g. Caneast Trucking Inc"
              data-testid="customer-label"
            />
          </div>
          <div>
            <div className="overline mb-1">COMPARE AGAINST NETWORK</div>
            <select
              className="gf-input"
              style={{ minWidth: 140 }}
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              data-testid="network-filter"
            >
              <option value="">Any (both)</option>
              <option value="esso">Esso only</option>
              <option value="pipeline">Pipeline only</option>
            </select>
          </div>
          <div>
            <div className="overline mb-1">GETFUEL PRICES AS OF</div>
            <input
              type="date"
              className="gf-input"
              style={{ minWidth: 160 }}
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              data-testid="as-of-date"
              title="Override the competitor transaction date — use our prices on THIS date for every row. Leave blank to match each row's own date."
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="sales-file-input"
          />
          <button
            className="gf-btn gf-btn-primary"
            onClick={pickFile}
            disabled={analyzing}
            data-testid="upload-file-button"
          >
            <UploadSimple size={14}/> {analyzing ? "Analysing…" : "Upload file"}
          </button>
        </div>
        {filename && !analyzing && (
          <div className="text-xs text-zinc-500 mt-3 font-mono">
            LAST FILE · {filename}
            {(network || asOfDate) && (
              <span className="ml-3">
                FILTERS · {network ? `network=${network}` : "all networks"}{asOfDate ? ` · as-of ${asOfDate}` : ""}
              </span>
            )}
          </div>
        )}
        {analyzing && (
          <div className="text-xs text-zinc-500 mt-3 font-mono flex items-center gap-2">
            <Sparkle size={12} weight="fill" className="animate-spin"/> EXTRACTING {mode === "transactions" ? "TRANSACTIONS" : "PRICE LIST"} · AI MAY TAKE UP TO 60s FOR PDFs
          </div>
        )}
        <div className="text-[11px] text-zinc-400 mt-3 italic">
          Analyses are auto-deleted after 24 hours (privacy retention). Delete manually anytime from the list below.
        </div>
      </div>

      {/* ---- Recent uploads (24h retention) ---- */}
      {analyses.length > 0 && (
        <div className="gf-card mb-4">
          <div className="gf-card-header flex items-center justify-between">
            <h3>Recent uploads ({analyses.length})</h3>
            <div className="flex items-center gap-1">
              <button
                className="gf-btn gf-btn-outline text-red-700 border-red-300 hover:bg-red-50 text-xs"
                onClick={bulkDeleteAnalyses}
                data-testid="bulk-delete-analyses"
                title="Clear all recent uploads"
              >
                <Trash size={12}/> Delete all
              </button>
              <button
                className="gf-btn gf-btn-ghost text-xs"
                onClick={loadAnalyses}
                data-testid="refresh-analyses"
                title="Refresh the list"
              >
                <ArrowClockwise size={14}/>
              </button>
            </div>
          </div>
          <div className="gf-card-body overflow-x-auto">
            <table className="gf-table text-xs">
              <thead><tr>
                <th>Created</th><th>Customer</th><th>File</th><th>Mode</th>
                <th>Network</th><th>As-of</th>
                <th className="text-right">Rows</th>
                <th className="text-right">Ann. savings</th>
                <th></th>
              </tr></thead>
              <tbody>
                {analyses.map((a) => (
                  <tr key={a.analysis_id} data-testid={`analysis-row-${a.analysis_id}`}>
                    <td className="font-mono">{(a.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td>{a.customer_label || <span className="text-zinc-400">—</span>}</td>
                    <td className="truncate max-w-[260px]">{a.filename}</td>
                    <td><Badge variant="muted">{a.mode}</Badge></td>
                    <td className="font-mono">{a.network || "any"}</td>
                    <td className="font-mono">{a.as_of_date || "—"}</td>
                    <td className="text-right font-mono">{a.summary?.row_count || 0}</td>
                    <td className={`text-right font-mono ${(a.summary?.annualised_savings || 0) > 0 ? "text-emerald-700" : (a.summary?.annualised_savings || 0) < 0 ? "text-red-700" : ""}`}>
                      {a.summary?.annualised_savings != null
                        ? `$${a.summary.annualised_savings >= 0 ? "+" : ""}${Number(a.summary.annualised_savings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "—"}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        className="gf-btn gf-btn-ghost text-xs"
                        onClick={() => loadAnalysis(a.analysis_id)}
                        data-testid={`open-analysis-${a.analysis_id}`}
                        title="Open this analysis"
                      >
                        Open
                      </button>
                      <button
                        className="gf-btn gf-btn-ghost text-xs text-red-700"
                        onClick={() => deleteAnalysis(a.analysis_id)}
                        data-testid={`delete-analysis-${a.analysis_id}`}
                        title="Delete this analysis"
                      >
                        <Trash size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Results ---- */}
      {result && !isPriceList && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div className="gf-card gf-card-body border-emerald-200">
              <div className="overline text-emerald-700">PROJECTED ANNUAL SAVINGS</div>
              <div className="text-3xl font-mono font-semibold text-emerald-700 mt-1" data-testid="annualised-savings">
                ${(s.annualised_savings ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-zinc-500 mt-1">extrapolated from {s.projection_days} days of data</div>
            </div>
            <div className="gf-card gf-card-body">
              <div className="overline">ANALYSED PERIOD</div>
              <div className="text-sm font-mono mt-1">
                ${(s.total_savings ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {(s.date_range?.[0]) ? `${s.date_range[0]} → ${s.date_range[1]}` : "—"}
              </div>
            </div>
            <div className="gf-card gf-card-body">
              <div className="overline">VOLUME ANALYSED</div>
              <div className="text-sm font-mono mt-1">
                {(s.total_volume_l ?? 0).toLocaleString()} L
              </div>
              <div className="text-xs text-zinc-500 mt-1">coverage {s.coverage_pct ?? 0}%</div>
            </div>
            <div className="gf-card gf-card-body">
              <div className="overline">AVG SAVING / LITRE</div>
              <div className="text-sm font-mono mt-1">
                {s.avg_saving_cpl !== null && s.avg_saving_cpl !== undefined
                  ? `$${s.avg_saving_cpl >= 0 ? "+" : ""}${Number(s.avg_saving_cpl).toFixed(4)}`
                  : "—"}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {s.tier_counts ? `station ${(s.tier_counts.station || 0) + (s.tier_counts.station_latest || 0)} · city ${(s.tier_counts.city || 0) + (s.tier_counts.city_latest || 0)} · province ${(s.tier_counts.province || 0) + (s.tier_counts.province_latest || 0)}` : ""}
              </div>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <button className="gf-btn gf-btn-outline" onClick={() => downloadReport("pdf")} data-testid="download-pdf-button">
              <FilePdf size={14}/> Download PDF pitch
            </button>
            <button className="gf-btn gf-btn-outline" onClick={() => downloadReport("csv")} data-testid="download-csv-button">
              <FileCsv size={14}/> Download CSV detail
            </button>
          </div>

          <CheapestStations rows={result.top_cheapest_stations || []} />
          <ByCityPanel rows={result.by_city || []} />
          <TransactionDetailTable rows={result.detail || []} />
        </>
      )}

      {result && isPriceList && (
        <>
          <div className="flex gap-2 mb-4">
            <button className="gf-btn gf-btn-outline" onClick={() => downloadReport("pdf")} data-testid="download-pdf-button">
              <FilePdf size={14}/> Download PDF pitch
            </button>
          </div>
          <CheapestStations rows={result.top_cheapest_stations || []} />
          <PriceListTable byStation={result.by_station || []} byCity={result.by_city || []} />
        </>
      )}

      {result && result.warnings?.length > 0 && (
        <div className="gf-card gf-card-body mt-4 border-amber-200 bg-amber-50">
          <div className="overline text-amber-800 mb-2">WARNINGS</div>
          {result.warnings.map((w, i) => (
            <div key={i} className="text-xs text-amber-900">• {w.message}</div>
          ))}
        </div>
      )}

      {!result && !analyzing && (
        <div className="gf-card gf-card-body bg-zinc-50 text-center py-12 text-zinc-500">
          <Target size={40} className="mx-auto mb-3 text-zinc-400"/>
          <div className="text-sm mb-1">Upload a competitor transaction or pricing file to see projected savings.</div>
          <div className="text-xs font-mono text-zinc-400">CSV · XLSX · PDF accepted</div>
        </div>
      )}
    </SectionPad>
  );
}

function CheapestStations({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="gf-card mb-4">
      <div className="gf-card-header"><h3>Top {rows.length} cheapest getFuel stations</h3></div>
      <div className="gf-card-body overflow-x-auto">
        <table className="gf-table">
          <thead><tr>
            <th>#</th><th>Station</th><th>Network</th><th>City</th><th>Prov</th><th className="text-right">Avg $/L</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.station_id || i} data-testid={`cheapest-row-${i}`}>
                <td className="font-mono text-xs text-zinc-500">{i + 1}</td>
                <td>{r.station_name || r.station_id || "—"}</td>
                <td><Badge variant={r.network === "esso" ? "warning" : "muted"}>{r.network}</Badge></td>
                <td>{r.city || "—"}</td>
                <td className="font-mono">{r.province || ""}</td>
                <td className="text-right font-mono">${(r.avg_cpl || 0).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TransactionDetailTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="gf-card">
      <div className="gf-card-header"><h3>Transaction detail ({rows.length})</h3></div>
      <div className="gf-card-body overflow-x-auto">
        <table className="gf-table text-xs">
          <thead><tr>
            <th>Date</th><th>Station</th><th>City/Prov</th><th>Product</th>
            <th className="text-right">Litres</th>
            <th className="text-right">Comp $/L</th>
            <th className="text-right">GF $/L</th>
            <th className="text-right">Delta</th>
            <th className="text-right">Savings</th>
            <th>Match</th>
          </tr></thead>
          <tbody>
            {rows.slice(0, 500).map((r, i) => (
              <tr key={i} data-testid={`detail-row-${i}`}>
                <td className="font-mono">{r.date || "—"}</td>
                <td>{r.station_name || r.station_id || "—"}</td>
                <td>{r.city || "—"} / {r.province || ""}</td>
                <td>{r.product_canonical}</td>
                <td className="text-right font-mono">{(r.volume_l || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                <td className="text-right font-mono">${(r.unit_price_cpl || 0).toFixed(4)}</td>
                <td className="text-right font-mono">{r.getfuel_cpl != null ? `$${r.getfuel_cpl.toFixed(4)}` : "—"}</td>
                <td className={`text-right font-mono ${r.delta_cpl > 0 ? "text-emerald-700" : r.delta_cpl < 0 ? "text-red-700" : ""}`}>
                  {r.delta_cpl != null ? `${r.delta_cpl >= 0 ? "+" : ""}${r.delta_cpl.toFixed(4)}` : "—"}
                </td>
                <td className={`text-right font-mono ${r.savings > 0 ? "text-emerald-700" : r.savings < 0 ? "text-red-700" : ""}`}>
                  {r.savings != null ? `$${r.savings >= 0 ? "+" : ""}${r.savings.toFixed(2)}` : "—"}
                </td>
                <td className="text-xs text-zinc-500">{r.match_tier}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 500 && <div className="text-xs text-zinc-500 mt-2 italic">Showing first 500 of {rows.length} rows. Full list in the CSV download.</div>}
      </div>
    </div>
  );
}

function PriceListTable({ byStation, byCity }) {
  return (
    <>
      {byCity.length > 0 && (
        <div className="gf-card mb-4">
          <div className="gf-card-header"><h3>City-by-city $/L delta ({byCity.length})</h3></div>
          <div className="gf-card-body overflow-x-auto">
            <table className="gf-table text-xs">
              <thead><tr><th></th><th>City</th><th>Product</th><th className="text-right">Stations</th><th className="text-right">Avg delta $/L</th></tr></thead>
              <tbody>
                {byCity.map((c, i) => {
                  // Price-list mode: avg_delta_cpl = competitor - getFuel.
                  // Positive delta means competitor is MORE expensive, so WE win.
                  const weWin = c.avg_delta_cpl > 0.01;
                  const theyWin = c.avg_delta_cpl < -0.01;
                  return (
                    <tr key={i}>
                      <td className="w-24">
                        {weWin ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 font-mono text-[10px] uppercase tracking-widest border border-emerald-300">
                            <CheckCircle size={10} weight="fill"/> We win
                          </span>
                        ) : theyWin ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 font-mono text-[10px] uppercase tracking-widest border border-red-300">
                            <XCircle size={10} weight="fill"/> They win
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-zinc-100 text-zinc-700 font-mono text-[10px] uppercase tracking-widest border border-zinc-300">Tie</span>
                        )}
                      </td>
                      <td>{c.city}</td>
                      <td>{c.product}</td>
                      <td className="text-right font-mono">{c.station_count}</td>
                      <td className={`text-right font-mono ${c.avg_delta_cpl > 0 ? "text-emerald-700" : c.avg_delta_cpl < 0 ? "text-red-700" : ""}`}>
                        {c.avg_delta_cpl >= 0 ? "+" : ""}${c.avg_delta_cpl.toFixed(4)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {byStation.length > 0 && (
        <div className="gf-card">
          <div className="gf-card-header"><h3>Station-by-station comparison ({byStation.length})</h3></div>
          <div className="gf-card-body overflow-x-auto">
            <table className="gf-table text-xs">
              <thead><tr><th>Station</th><th>City</th><th>Prov</th><th>Product</th><th className="text-right">Comp $/L</th><th className="text-right">GF $/L</th><th className="text-right">Delta</th><th>Match</th></tr></thead>
              <tbody>
                {byStation.slice(0, 500).map((r, i) => (
                  <tr key={i}>
                    <td>{r.station_name || r.station_id || "—"}</td>
                    <td>{r.city || "—"}</td>
                    <td className="font-mono">{r.province || ""}</td>
                    <td>{r.product_canonical}</td>
                    <td className="text-right font-mono">${(r.unit_price_cpl || 0).toFixed(4)}</td>
                    <td className="text-right font-mono">{r.getfuel_cpl != null ? `$${r.getfuel_cpl.toFixed(4)}` : "—"}</td>
                    <td className={`text-right font-mono ${r.delta_cpl > 0 ? "text-emerald-700" : r.delta_cpl < 0 ? "text-red-700" : ""}`}>
                      {r.delta_cpl != null ? `${r.delta_cpl >= 0 ? "+" : ""}${r.delta_cpl.toFixed(4)}` : "—"}
                    </td>
                    <td className="text-xs text-zinc-500">{r.match_tier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}


// ---------- City advantage panel ----------
// Big, scannable green/red pills per city so the sales user can instantly
// tell where getFuel wins and where the incumbent wins. Sorted with wins
// at the top so pitch meetings lead with wins.
function ByCityPanel({ rows }) {
  if (!rows?.length) return null;
  const wins = rows.filter((r) => r.advantage === "gf");
  const losses = rows.filter((r) => r.advantage === "competitor");
  const ties = rows.filter((r) => r.advantage === "tie");
  return (
    <div className="gf-card mb-4" data-testid="by-city-panel">
      <div className="gf-card-header flex items-center justify-between">
        <h3>Savings by city</h3>
        <div className="text-xs font-mono text-zinc-500">
          <span className="text-emerald-700">{wins.length} cheaper</span>
          <span className="mx-2">·</span>
          <span className="text-red-700">{losses.length} pricier</span>
          {ties.length > 0 && <><span className="mx-2">·</span><span className="text-zinc-500">{ties.length} tie</span></>}
        </div>
      </div>
      <div className="gf-card-body overflow-x-auto">
        <table className="gf-table text-xs">
          <thead><tr>
            <th></th>
            <th>City</th>
            <th>Prov</th>
            <th className="text-right">Txns</th>
            <th className="text-right">Litres</th>
            <th className="text-right">Avg delta $/L</th>
            <th className="text-right">Savings</th>
          </tr></thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={i} data-testid={`city-row-${c.city}`}>
                <td className="w-24">
                  {c.advantage === "gf" ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 font-mono text-[10px] uppercase tracking-widest border border-emerald-300"
                      data-testid={`city-advantage-gf-${c.city}`}
                    >
                      <CheckCircle size={10} weight="fill"/> We win
                    </span>
                  ) : c.advantage === "competitor" ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 font-mono text-[10px] uppercase tracking-widest border border-red-300"
                      data-testid={`city-advantage-competitor-${c.city}`}
                    >
                      <XCircle size={10} weight="fill"/> They win
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 bg-zinc-100 text-zinc-700 font-mono text-[10px] uppercase tracking-widest border border-zinc-300">
                      Tie
                    </span>
                  )}
                </td>
                <td>{c.city || "—"}</td>
                <td className="font-mono">{c.province || ""}</td>
                <td className="text-right font-mono">{c.row_count}</td>
                <td className="text-right font-mono">{c.volume_l.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className={`text-right font-mono ${c.avg_delta_cpl > 0 ? "text-emerald-700" : c.avg_delta_cpl < 0 ? "text-red-700" : ""}`}>
                  {c.avg_delta_cpl >= 0 ? "+" : ""}${c.avg_delta_cpl.toFixed(4)}
                </td>
                <td className={`text-right font-mono ${c.savings > 0 ? "text-emerald-700" : c.savings < 0 ? "text-red-700" : ""}`}>
                  ${c.savings >= 0 ? "+" : ""}{c.savings.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
